import {Component, Injectable, signal, Type} from '@angular/core';
import {Observable, timer} from 'rxjs';

const DEFAULT_DURATION = 8000

type ToastType = 'success' | 'error' | 'info' | 'warning'
type SuccessHandler<T> = (result: T) => void

interface BaseToastInfo {
  header: string
  type?: ToastType
  duration?: number
}

interface SimpleToastInfo extends BaseToastInfo {
  body: string
}

interface ComponentToastInfo extends BaseToastInfo {
  component: Type<any>,
  inputs?: {[key: string]: any}
}

export type ToastInfo = SimpleToastInfo | ComponentToastInfo

export interface HandleResultOptions<T = any> {
  showSuccess?: boolean
  showError?: boolean
  successMessage?: string | ((result: T) => string)
  errorMessage?: string | ((error: string) => string)
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private static readonly DEFAULT_SUCCESS_MESSAGE = 'Operation successful'

  readonly toasts = signal<Array<ToastInfo>>([])

  constructor() {
  }

  add(header: string, body: string, type?: ToastType): void
  add(toast: ToastInfo): void
  add(toastOrHeader: ToastInfo | string, body?: string, type?: ToastType) {
    const toast: ToastInfo = (typeof toastOrHeader === 'string') ? {
      header: toastOrHeader,
      body: body!,
      type: type || 'info',
      duration: DEFAULT_DURATION
    } : Object.assign({duration: DEFAULT_DURATION, type: 'info'}, toastOrHeader)

    this.toasts.update(t => [...t, toast])

    if (toast.duration && toast.duration > 0) {
      timer(toast.duration).subscribe(() => this.remove(toast))
    }
  }

  remove(toast: ToastInfo) {
    this.toasts.update(t => t.filter(t => t !== toast))
  }

  success(header: string, body: string) {
    this.add(header, body, 'success')
  }

  error(header: string, body: string) {
    this.add(header, body, 'error')
  }

  info(message: string) {
    this.add('Info', message, 'info')
  }

  handleResult<T>(header: string, operation: Observable<T>): Observable<T>
  handleResult<T>(header: string, operation: Observable<T>, next: SuccessHandler<T>): Observable<T>
  handleResult<T>(header: string, operation: Observable<T>, options: HandleResultOptions<T>): Observable<T>
  handleResult<T>(header: string, operation: Observable<T>, next: SuccessHandler<T>, options: HandleResultOptions<T>): Observable<T>
  handleResult<T>(
    header: string,
    operation: Observable<T>,
    next?: HandleResultOptions | SuccessHandler<T>,
    options?: HandleResultOptions
  ): Observable<T> {
    if (typeof next === 'object') {
      options = next
      next = undefined
    }

    operation.subscribe({
      next: (result) => {
        if (options?.showSuccess !== false) {
          this.success(
            header,
            this.toMessage(options?.successMessage || ToastService.DEFAULT_SUCCESS_MESSAGE, result)
          )
        }
        if (next) {
          next(result)
        }
      },
      error: (error) => {
        const message = this.toMessage(options?.errorMessage || ((err) => this.defaultErrorMessage(err)), error)
        console.error(message, error)
        this.error(header, message)
      }
    })
    return operation
  }

  errorToString(error: any): string {
    if (typeof error === 'string') {
      return error
    } else if ('message' in error) {
      return error.message
    } else if (typeof error === 'object' && error !== null) {
      return JSON.stringify(error)
    } else {
      return 'Unknown error'
    }
  }

  isSimpleToast(toast: ToastInfo): toast is SimpleToastInfo {
    return 'body' in toast
  }

  isComponentToast(toast: ToastInfo): toast is ComponentToastInfo {
    return 'component' in toast
  }

  private toMessage<T>(message: string | ((context: T) => string), context: T): string {
    if (typeof message === 'string') return message
    return message(context)
  }

  private defaultErrorMessage(error: any): string {
    return `Operation failed: ${this.errorToString(error)}`
  }
}
